<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal px-4 py-3 md:px-6">
        <div>
          <h1 class="list-page-title">腾讯电子签资源</h1>
          <p class="mt-2 text-sm leading-6 text-slate-600">查询腾讯侧模板、企业印章、用户实名状态，并维护应用回调配置。</p>
        </div>
      </div>
    </template>

    <div class="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="腾讯模板" name="templates">
          <PageFilterBar shell-class="filter-bar-shell is-embedded" body-class="filter-bar-body is-embedded" :show-refresh="true" :refresh-loading="templateLoading" @refresh="fetchTemplates">
            <div class="filter-bar-field is-wide relative">
              <input v-model="templateFilters.keyword" class="form-search-input form-control-sm w-full" placeholder="搜索模板名称或 ID" @keyup.enter="fetchTemplates" />
              <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <template #searchActions>
              <button class="btn-primary" type="button" @click="fetchTemplates">搜索</button>
            </template>
          </PageFilterBar>
          <ResourceTable :loading="templateLoading" :items="templates" type="template" />
        </el-tab-pane>

        <el-tab-pane label="企业印章" name="seals">
          <PageFilterBar shell-class="filter-bar-shell is-embedded" body-class="filter-bar-body is-embedded" :show-refresh="true" :refresh-loading="sealLoading" @refresh="fetchSeals">
            <div class="filter-bar-field is-wide relative">
              <input v-model="sealFilters.keyword" class="form-search-input form-control-sm w-full" placeholder="搜索印章名称或 ID" @keyup.enter="fetchSeals" />
              <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <template #searchActions>
              <button class="btn-primary" type="button" @click="fetchSeals">搜索</button>
            </template>
          </PageFilterBar>
          <ResourceTable :loading="sealLoading" :items="seals" type="seal" />
        </el-tab-pane>

        <el-tab-pane label="实名状态" name="verify">
          <div class="grid gap-4 lg:grid-cols-[420px_1fr]">
            <div class="rounded-2xl border border-slate-200 p-4">
              <h3 class="text-sm font-semibold text-slate-900">实名状态查询</h3>
              <div class="mt-4 space-y-3">
                <input v-model="verifyForm.name" class="form-input w-full" placeholder="姓名" />
                <input v-model="verifyForm.mobile" class="form-input w-full" placeholder="手机号" />
                <input v-model="verifyForm.idCardNumber" class="form-input w-full" placeholder="证件号，可选" />
                <button class="btn-primary w-full" type="button" :disabled="verifyLoading" @click="queryVerify">查询实名状态</button>
              </div>
            </div>
            <div class="rounded-2xl border border-slate-200 p-4">
              <h3 class="text-sm font-semibold text-slate-900">查询结果</h3>
              <pre class="mt-4 max-h-[420px] overflow-auto rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">{{ safeJson(verifyResult) || '暂无查询结果' }}</pre>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="回调配置" name="callback">
          <div class="max-w-3xl rounded-2xl border border-slate-200 p-4">
            <h3 class="text-sm font-semibold text-slate-900">修改腾讯回调配置</h3>
            <div class="mt-4 space-y-4">
              <label class="block">
                <span class="text-sm text-slate-600">回调 URL</span>
                <input v-model="callbackForm.callbackUrl" class="form-input mt-1 w-full" placeholder="https://example.com/api/open/esign/tencent/callback" />
              </label>
              <label class="block">
                <span class="text-sm text-slate-600">Callback Key</span>
                <input v-model="callbackForm.callbackKey" class="form-input mt-1 w-full" placeholder="腾讯电子签回调密钥" />
              </label>
              <label class="block">
                <span class="text-sm text-slate-600">事件类型</span>
                <input v-model="callbackForm.eventTypes" class="form-input mt-1 w-full" placeholder="FLOW,APPROVER，可按后端约定填写" />
              </label>
              <label class="inline-flex items-center gap-2 text-sm text-slate-700">
                <input v-model="callbackForm.enabled" type="checkbox" class="form-check" />
                启用回调
              </label>
              <div>
                <button class="btn-primary" type="button" :disabled="callbackSubmitting" @click="submitCallback">保存回调配置</button>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </ListPageShell>
</template>

<script setup>
import { defineComponent, h, onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getTencentEsignSeals, getTencentEsignTemplates, modifyTencentCallback, queryTencentUserVerifyStatus } from '@/api/esign'
import { blank, readArray, safeJson, statusClassByResult } from './esign-utils'

const activeTab = ref('templates')
const templateLoading = ref(false)
const sealLoading = ref(false)
const verifyLoading = ref(false)
const callbackSubmitting = ref(false)
const templates = ref([])
const seals = ref([])
const verifyResult = ref(null)
const templateFilters = reactive({ keyword: '' })
const sealFilters = reactive({ keyword: '' })
const verifyForm = reactive({ name: '', mobile: '', idCardNumber: '' })
const callbackForm = reactive({ callbackUrl: '', callbackKey: '', eventTypes: 'FLOW,APPROVER', enabled: true })

const ResourceTable = defineComponent({
  name: 'ResourceTable',
  props: {
    loading: Boolean,
    items: { type: Array, default: () => [] },
    type: { type: String, default: 'template' }
  },
  setup(props) {
    return () => h('div', { class: 'data-table-shell' }, [
      h('div', { class: 'data-table-scroll' }, [
        h('table', { class: 'data-table text-sm' }, [
          h('thead', { class: 'data-table-head' }, [
            h('tr', [
              h('th', { class: 'data-table-th text-left' }, props.type === 'seal' ? '印章 ID' : '模板 ID'),
              h('th', { class: 'data-table-th text-left' }, props.type === 'seal' ? '印章名称' : '模板名称'),
              h('th', { class: 'data-table-th text-left' }, props.type === 'seal' ? '类型' : '状态'),
              h('th', { class: 'data-table-th text-left' }, '预览')
            ])
          ]),
          h('tbody', props.loading
            ? [h('tr', { class: 'data-table-row' }, [h('td', { class: 'data-table-empty', colspan: 4 }, '正在加载...')])]
            : props.items.length
              ? props.items.map(item => h('tr', { class: 'data-table-row', key: item.templateId || item.sealId || item.id }, [
                h('td', { class: 'data-table-td font-mono text-xs' }, props.type === 'seal' ? blank(item.sealId || item.id) : blank(item.templateId || item.id)),
                h('td', { class: 'data-table-td' }, props.type === 'seal' ? blank(item.sealName || item.name) : blank(item.templateName || item.name)),
                h('td', { class: 'data-table-td' }, [
                  h('span', { class: ['status-badge', statusClassByResult(item.status || item.sealStatus)] }, blank(props.type === 'seal' ? (item.sealType || item.sealStatus || item.status) : item.status))
                ]),
                h('td', { class: 'data-table-td' }, item.previewUrl ? h('a', { class: 'table-action-button is-primary', href: item.previewUrl, target: '_blank', rel: 'noreferrer' }, '打开') : '-')
              ]))
              : [h('tr', { class: 'data-table-row' }, [h('td', { class: 'data-table-empty', colspan: 4 }, '暂无数据')])])
        ])
      ])
    ])
  }
})

const fetchTemplates = async () => {
  templateLoading.value = true
  try {
    templates.value = readArray(await getTencentEsignTemplates({ ...templateFilters }))
  } catch (error) {
    ElMessage.error(error.message || '查询腾讯模板失败')
  } finally {
    templateLoading.value = false
  }
}

const fetchSeals = async () => {
  sealLoading.value = true
  try {
    seals.value = readArray(await getTencentEsignSeals({ ...sealFilters }))
  } catch (error) {
    ElMessage.error(error.message || '查询企业印章失败')
  } finally {
    sealLoading.value = false
  }
}

const queryVerify = async () => {
  if (!verifyForm.mobile) {
    ElMessage.warning('请填写手机号')
    return
  }
  verifyLoading.value = true
  try {
    verifyResult.value = await queryTencentUserVerifyStatus({ ...verifyForm })
  } catch (error) {
    ElMessage.error(error.message || '查询实名状态失败')
  } finally {
    verifyLoading.value = false
  }
}

const submitCallback = async () => {
  if (!callbackForm.callbackUrl) {
    ElMessage.warning('请填写回调 URL')
    return
  }
  callbackSubmitting.value = true
  try {
    await modifyTencentCallback({ ...callbackForm })
    ElMessage.success('回调配置已提交')
  } catch (error) {
    ElMessage.error(error.message || '保存回调配置失败')
  } finally {
    callbackSubmitting.value = false
  }
}

onMounted(() => {
  fetchTemplates()
  fetchSeals()
})
</script>
